import React, { useState, useEffect } from 'react';
import DataModal from './DataModal';

const EventsUsersPanel = ({ selectedRegion, timeFrame, filterGenre, sortBy, isMobile = false }) => {
  const [eventsData, setEventsData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch events and users data
  const fetchData = async () => {
    try {
      const [eventsResponse, usersResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/area-activity/events?region_id=${selectedRegion.id}&timeframe=${timeFrame}`),
        fetch(`${BACKEND_URL}/api/area-activity/users?region_id=${selectedRegion.id}&timeframe=${timeFrame}`)
      ]);
      
      const events = await eventsResponse.json();
      const users = await usersResponse.json();
      
      setEventsData(events);
      setUsersData(users);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Initial fetch and polling every 30 seconds
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [selectedRegion.id, timeFrame]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: '#94a3b8'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* Modals */}
      {modalType === 'events' && (
        <DataModal
          title="UPCOMING EVENTS"
          icon="🎉"
          data={eventsData?.upcoming}
          onClose={() => setModalType(null)}
          accentColor="#FF00FF"
          renderItem={(event, idx) => (
            <div key={idx} style={{
              padding: '14px',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '10px',
              border: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#FF00FF',
                marginBottom: '6px'
              }}>
                {event.name}
              </div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px' }}>
                {event.artist}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                📍 {event.city} • {event.date} • {event.attendees} attending
              </div>
            </div>
          )}
        />
      )}

      {modalType === 'users' && (
        <DataModal
          title="ACTIVE NOW"
          icon="👥"
          data={usersData?.active_now}
          onClose={() => setModalType(null)}
          accentColor="#FF00FF"
          renderItem={(user, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#FF00FF',
                  marginBottom: '4px'
                }}>
                  {user.username}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {user.city} • {user.status}
                </div>
              </div>
              <div style={{
                fontSize: '11px',
                padding: '4px 10px',
                background: 'rgba(255, 0, 255, 0.15)',
                borderRadius: '12px',
                color: '#FF00FF',
                fontWeight: '600'
              }}>
                {user.genre}
              </div>
            </div>
          )}
        />
      )}

      {/* SLOT 1 - Live Events (Live only) OR Hotspots (Historical) */}
      <div style={{
        background: 'rgba(255, 0, 255, 0.05)',
        border: '2px solid rgba(255, 0, 255, 0.3)',
        borderRadius: '12px',
        padding: '15px',
        boxShadow: '0 0 15px rgba(255, 0, 255, 0.2)',
        flexShrink: 0
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: '#FF00FF',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>{timeFrame === 'Live' ? '🎉' : '🏆'}</span>
          <span>{timeFrame === 'Live' ? 'LIVE EVENTS' : 'HOTSPOTS'}</span>
        </div>
        {timeFrame === 'Live' ? (
          // Live Events - only current/ongoing events
          eventsData?.live_events && eventsData.live_events.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {eventsData.live_events.slice(0, 2).map((event, idx) => (
                <div key={idx} style={{
                  padding: '8px',
                  background: 'rgba(255, 0, 255, 0.03)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 0, 255, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FF00FF',
                    marginBottom: '3px'
                  }}>
                    🔴 {event.name}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                    📍 {event.city} • {event.attendees} attending
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              fontSize: '12px',
              color: '#94a3b8',
              textAlign: 'center',
              padding: '20px 10px',
              fontStyle: 'italic'
            }}>
              No events going on here at this time
            </div>
          )
        ) : (
          // Hotspots for historical timeframes
          usersData?.hotspots && usersData.hotspots.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {usersData.hotspots.slice(0, 2).map((spot, idx) => (
                <div key={idx} style={{
                  padding: '8px',
                  background: 'rgba(255, 0, 255, 0.03)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 0, 255, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FF00FF',
                    marginBottom: '3px'
                  }}>
                    📍 {spot.location}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                    {spot.activity_count} activities • {spot.genre}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px' }}>
              Loading...
            </div>
          )
        )}
      </div>

      {/* SLOT 2 - Live Check-ins (Live only) OR Active (Historical) */}
      <div style={{
        background: 'rgba(255, 0, 255, 0.05)',
        border: '2px solid rgba(255, 0, 255, 0.3)',
        borderRadius: '12px',
        padding: '15px',
        boxShadow: '0 0 15px rgba(255, 0, 255, 0.2)',
        flexShrink: 0
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: '#FF00FF',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{timeFrame === 'Live' ? '📍' : '👥'}</span>
            <span>{timeFrame === 'Live' ? 'LIVE CHECK-INS' : 'ACTIVE'}</span>
          </div>
          {timeFrame !== 'Live' && (
            <button
              onClick={() => setModalType('users')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#FF00FF',
                fontSize: '11px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '0'
              }}
            >
              View All
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {usersData?.active_now?.slice(0, 2).map((user, idx) => (
            <div key={idx} style={{
              padding: '6px',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '6px',
              borderLeft: timeFrame === 'Live' ? '2px solid rgba(255, 0, 255, 0.5)' : 'none'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#FF00FF',
                marginBottom: '2px'
              }}>
                {user.username}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                {timeFrame === 'Live' 
                  ? `📍 ${user.city} • ${Math.floor(Math.random() * 30 + 1)}m ago`
                  : `${user.city} • ${user.status}`
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SLOT 3 - Active Now (Live) OR Upcoming Events (Historical) */}
      <div style={{
        background: 'rgba(255, 0, 255, 0.05)',
        border: '2px solid rgba(255, 0, 255, 0.3)',
        borderRadius: '12px',
        padding: '15px',
        boxShadow: '0 0 15px rgba(255, 0, 255, 0.2)',
        flexShrink: 0
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: '#FF00FF',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{timeFrame === 'Live' ? '👥' : '🎉'}</span>
            <span>{timeFrame === 'Live' ? 'ACTIVE NOW' : 'UPCOMING EVENTS'}</span>
          </div>
          <button
            onClick={() => setModalType(timeFrame === 'Live' ? 'users' : 'events')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#FF00FF',
              fontSize: '11px',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '0'
            }}
          >
            View All
          </button>
        </div>
        {timeFrame === 'Live' ? (
          // Active Now for Live timeframe
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {usersData?.active_now?.slice(0, 2).map((user, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px',
                background: 'rgba(255, 0, 255, 0.03)',
                borderRadius: '6px'
              }}>
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FF00FF'
                  }}>
                    {user.username}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                    {user.city}
                  </div>
                </div>
                <div style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  background: 'rgba(255, 0, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#FF00FF'
                }}>
                  {user.genre}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Upcoming Events for historical timeframes
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {eventsData?.upcoming?.slice(0, 2).map((event, idx) => (
              <div key={idx} style={{
                padding: '8px',
                background: 'rgba(255, 0, 255, 0.03)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 255, 0.2)'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#FF00FF',
                  marginBottom: '3px'
                }}>
                  {event.name}
                </div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                  📍 {event.city} • {event.date}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default EventsUsersPanel;
